import { RouteApi, Route, type RouteListMetrics } from '../models';
import { computeDeliveryCountsFromPercentages } from '../utils/computeDeliveryCountsFromPercentages';

type RouteApiPayload = RouteApi & {
  scheduled_calendar_date?: string;
  commerce_orders_count?: number;
  visits_completed_count?: number;
  visits_failed_count?: number;
  visits_pending_count?: number;
  visits_expired_count?: number;
  metrics?: RouteListMetrics & {
    total_orders?: number;
    delivered_percentage?: number;
    failed_percentage?: number;
    pending_percentage?: number;
    expired_percentage?: number;
  };
};

function pickCommerceOrdersCount(payload: RouteApiPayload): number | undefined {
  if (typeof payload.commerceOrdersCount === 'number') {
    return payload.commerceOrdersCount;
  }
  if (typeof payload.commerce_orders_count === 'number') {
    return payload.commerce_orders_count;
  }
  return undefined;
}

function pickScheduledCalendarDate(
  payload: RouteApiPayload
): string | undefined {
  const camel = payload.scheduledCalendarDate;
  if (typeof camel === 'string' && camel.trim() !== '') {
    return camel.trim();
  }
  const snake = payload.scheduled_calendar_date;
  if (typeof snake === 'string' && snake.trim() !== '') {
    return snake.trim();
  }
  return undefined;
}

function pickRouteListMetrics(
  payload: RouteApiPayload
): RouteListMetrics | undefined {
  const m = payload.metrics;
  if (!m || typeof m !== 'object') {
    return undefined;
  }
  const totalOrders = Number(
    typeof m.totalOrders === 'number'
      ? m.totalOrders
      : (m as { total_orders?: number }).total_orders
  );
  const deliveredPercentage = Number(
    typeof m.deliveredPercentage === 'number'
      ? m.deliveredPercentage
      : (m as { delivered_percentage?: number }).delivered_percentage
  );
  const failedPercentage = Number(
    typeof m.failedPercentage === 'number'
      ? m.failedPercentage
      : (m as { failed_percentage?: number }).failed_percentage
  );
  const pendingPercentage = Number(
    typeof m.pendingPercentage === 'number'
      ? m.pendingPercentage
      : (m as { pending_percentage?: number }).pending_percentage
  );
  const expiredPercentage = Number(
    typeof (m as any).expiredPercentage === 'number'
      ? (m as any).expiredPercentage
      : (m as { expired_percentage?: number }).expired_percentage
  );
  if (
    !Number.isFinite(totalOrders) ||
    !Number.isFinite(deliveredPercentage) ||
    !Number.isFinite(failedPercentage) ||
    !Number.isFinite(pendingPercentage)
  ) {
    return undefined;
  }
  return {
    totalOrders,
    deliveredPercentage,
    failedPercentage,
    pendingPercentage,
    expiredPercentage: Number.isFinite(expiredPercentage) ? expiredPercentage : 0,
  };
}

function visitStatusCountsFromRoutePayload(
  payload: RouteApiPayload
): { completed: number; failed: number; pending: number; expired: number } | null {
  const metrics = pickRouteListMetrics(payload);
  if (!metrics) {
    return null;
  }
  const { delivered, failed, pending, expired } = computeDeliveryCountsFromPercentages(
    metrics.totalOrders,
    metrics.deliveredPercentage,
    metrics.failedPercentage,
    metrics.pendingPercentage,
    metrics.expiredPercentage
  );
  return { completed: delivered, failed, pending, expired };
}

function pickVisitCountsOverride(
  payload: RouteApiPayload
): { completed: number; failed: number; pending: number; expired: number } | null {
  const completedRaw =
    payload.visitsCompletedCount ?? payload.visits_completed_count;
  const failedRaw = payload.visitsFailedCount ?? payload.visits_failed_count;
  const pendingRaw = payload.visitsPendingCount ?? payload.visits_pending_count;
  const expiredRaw = payload.visitsExpiredCount ?? payload.visits_expired_count;
  if (
    typeof completedRaw !== 'number' ||
    typeof failedRaw !== 'number' ||
    typeof pendingRaw !== 'number' ||
    !Number.isFinite(completedRaw) ||
    !Number.isFinite(failedRaw) ||
    !Number.isFinite(pendingRaw) ||
    completedRaw < 0 ||
    failedRaw < 0 ||
    pendingRaw < 0
  ) {
    return null;
  }
  return {
    completed: Math.floor(completedRaw),
    failed: Math.floor(failedRaw),
    pending: Math.floor(pendingRaw),
    expired: typeof expiredRaw === 'number' && expiredRaw >= 0 ? Math.floor(expiredRaw) : 0,
  };
}

export function mapRouteApiToModel(routeApi: RouteApi): Route {
  const scheduledCalendarDate = pickScheduledCalendarDate(
    routeApi as RouteApiPayload
  );
  const payload = routeApi as RouteApiPayload;
  const visitCountsOverride = pickVisitCountsOverride(payload);
  const visitCountsFromMetrics = visitStatusCountsFromRoutePayload(payload);
  const visitCounts = visitCountsOverride ?? visitCountsFromMetrics;

  return {
    id: routeApi.id,
    name: routeApi.name,
    driverId: routeApi.driverId,
    logisticOperatorId: routeApi.logisticOperatorId,
    scheduled: routeApi.scheduled,
    scheduledCalendarDate,
    status: routeApi.status,
    totalSeconds: routeApi.totalSeconds,
    source: routeApi.source,
    createdAt: routeApi.createdAt,
    updatedAt: routeApi.updatedAt,
    startedAt: routeApi.startedAt ?? null,
    closedAt: routeApi.closedAt ?? null,
    ordersCount:
      typeof routeApi.ordersCount === 'number' ? routeApi.ordersCount : 0,
    commerceOrdersCount: pickCommerceOrdersCount(routeApi as RouteApiPayload),
    visitsCompletedCount: visitCounts?.completed ?? null,
    visitsFailedCount: visitCounts?.failed ?? null,
    visitsPendingCount: visitCounts?.pending ?? null,
    visitsExpiredCount: visitCounts?.expired ?? null,
    driver: routeApi.driver,
    logisticOperator: routeApi.logisticOperator ?? null,
  };
}
